import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import Hls from 'hls.js';
import { MediaPlayer } from 'dashjs';

function VideoPlayer({
  hlsUrl,
  dashUrl,
  title = 'Video Stream',
  autoPlay = false,
  controls = true,
  width = '100%',
  height = 'auto',
  onError,
  onLoadStart,
  onLoadedData,
}) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const dashPlayerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentFormat, setCurrentFormat] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Cleanup previous players
    cleanup();

    // Determine which format to use (prefer HLS if both are available)
    let useHls = false;
    let useDash = false;

    if (hlsUrl && Hls.isSupported()) {
      useHls = true;
      setCurrentFormat('HLS');
    } else if (hlsUrl && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      video.src = hlsUrl;
      setCurrentFormat('HLS (Native)');
    } else if (dashUrl) {
      useDash = true;
      setCurrentFormat('DASH');
    } else {
      setError('No supported video format available');
      setIsLoading(false);
      return;
    }

    try {
      if (useHls) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90,
        });

        hls.loadSource(hlsUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
          setError(null);
          onLoadedData?.();
          if (autoPlay) {
            video.play().catch(e => console.warn('Autoplay failed:', e));
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS Error:', data);
          if (data.fatal) {
            setError(`HLS Error: ${data.details}`);
            onError?.(data);
          }
        });

        hlsRef.current = hls;
      } else if (useDash) {
        const dashPlayer = MediaPlayer().create();
        dashPlayer.initialize(video, dashUrl, autoPlay);

        dashPlayer.on('streamInitialized', () => {
          setIsLoading(false);
          setError(null);
          onLoadedData?.();
        });

        dashPlayer.on('error', (error) => {
          console.error('DASH Error:', error);
          setError(`DASH Error: ${error.error?.message || 'Unknown error'}`);
          onError?.(error);
        });

        dashPlayerRef.current = dashPlayer;
      }

      onLoadStart?.();
    } catch (err) {
      console.error('Player initialization error:', err);
      setError(`Failed to initialize player: ${err.message}`);
      setIsLoading(false);
    }

    // Video event listeners
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolumeChange = () => setIsMuted(video.muted);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('volumechange', handleVolumeChange);

    return cleanup;
  }, [hlsUrl, dashUrl, autoPlay, onError, onLoadStart, onLoadedData]);

  const cleanup = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (dashPlayerRef.current) {
      dashPlayerRef.current.destroy();
      dashPlayerRef.current = null;
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch(e => console.warn('Play failed:', e));
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen().catch(e => console.warn('Fullscreen failed:', e));
    }
  };

  if (error) {
    return (
      <Box
        sx={{
          width,
          height: height === 'auto' ? 300 : height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: '80%' }}>
          <Typography variant="body2" component="div">
            <strong>Video Player Error</strong>
          </Typography>
          <Typography variant="caption" component="div">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width,
        height: height === 'auto' ? 'auto' : height,
        background: '#000',
        borderRadius: 2,
        overflow: 'hidden',
        border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: height === 'auto' ? 'auto' : '100%',
          display: 'block',
        }}
        controls={controls}
        muted={autoPlay} // Autoplay usually requires muted
        playsInline
      />

      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
          }}
        >
          <CircularProgress size={40} sx={{ mb: 2, color: '#00d4ff' }} />
          <Typography variant="body2">Loading {currentFormat || 'video'}...</Typography>
          {title && (
            <Typography variant="caption" sx={{ mt: 1, opacity: 0.7 }}>
              {title}
            </Typography>
          )}
        </Box>
      )}

      {/* Custom Controls Overlay (when browser controls are disabled) */}
      {!controls && !isLoading && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
            <IconButton onClick={togglePlay} sx={{ color: 'white' }}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
            <IconButton onClick={toggleMute} sx={{ color: 'white' }}>
              {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1 }} />

          {currentFormat && (
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.8 }}>
              {currentFormat}
            </Typography>
          )}

          <Tooltip title="Fullscreen">
            <IconButton onClick={toggleFullscreen} sx={{ color: 'white' }}>
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Title Overlay */}
      {title && currentFormat && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            right: 8,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: 'white',
              background: 'rgba(0, 0, 0, 0.6)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backdropFilter: 'blur(4px)',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'white',
              background: 'rgba(0, 0, 0, 0.6)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backdropFilter: 'blur(4px)',
            }}
          >
            {currentFormat}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default VideoPlayer;